import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { t } from 'ttag';
import Button from '../button/button';
import apiService from '../../data/service/apiService';

const Preferences = styled.section`
  background: #F8F5FF;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.5;

  h2 { font-size: 16px; margin: 0 0 8px; }
  p { margin: 0 0 8px; }
  button { height: auto; min-height: 44px; }
`;

const validPreference = data => data?.webOptOutAvailable === true && typeof data.smsNotificationsEnabled === 'boolean';

const SmsNotificationPreferences = ({ token, onLayoutChange }) => {
  const [preference, setPreference] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    const controller = new AbortController();
    setPreference(null);
    setSaving(false);
    setError(false);
    setLoadError(false);
    apiService.get('/notification_preferences', token, controller.signal).then(data => {
      if (controller.signal.aborted) return;
      if (typeof data?.webOptOutAvailable !== 'boolean' || typeof data?.smsNotificationsEnabled !== 'boolean') throw new Error('Preference unavailable');
      if (sessionStorage.getItem('token') === token) sessionStorage.setItem('webSmsOptOutAvailable', String(data.webOptOutAvailable));
      if (validPreference(data)) setPreference(data);
    }).catch(() => {
      if (!controller.signal.aborted) setLoadError(true);
    });
    return () => {
      generation.current += 1;
      controller.abort();
    };
  }, [token, loadAttempt]);

  useEffect(() => { onLayoutChange?.(); }, [preference, error, loadError, onLayoutChange]);

  const save = async () => {
    const requestGeneration = generation.current;
    const enabled = !preference.smsNotificationsEnabled;
    setSaving(true);
    setError(false);
    try {
      const data = await apiService.post('/notification_preferences', { smsNotificationsEnabled: enabled }, token);
      if (requestGeneration !== generation.current) return;
      if (!validPreference(data) || data.smsNotificationsEnabled !== enabled) throw new Error('Preference not saved');
      setPreference(data);
    } catch {
      if (requestGeneration === generation.current) setError(true);
    } finally {
      if (requestGeneration === generation.current) setSaving(false);
    }
  };

  if (!preference) {
    const eligibleSession = sessionStorage.getItem('token') === token && sessionStorage.getItem('webSmsOptOutAvailable') === 'true';
    return loadError && eligibleSession ? (
      <Preferences>
        <p role="alert">{t`Unable to load SMS settings. Please try again.`}</p>
        <Button title={t`Retry`} onClick={() => setLoadAttempt(attempt => attempt + 1)} />
      </Preferences>
    ) : null;
  }

  return (
    <Preferences aria-labelledby="sms-notification-heading">
      <h2 id="sms-notification-heading">{t`Case text messages`}</h2>
      <p>{t`This applies to all your cases. Login codes you request will still be sent.`}</p>
      <p role="status">{preference.smsNotificationsEnabled ? t`Case texts are on.` : t`Case texts are off.`}</p>
      {error && <p role="alert">{t`Unable to save SMS settings. Please try again.`}</p>}
      <Button disabled={saving} onClick={save} title={preference.smsNotificationsEnabled ? t`Turn off case texts` : t`Turn on case texts`} />
    </Preferences>
  );
};

export default SmsNotificationPreferences;
